import { UTCDate } from '@date-fns/utc';
import { useQueryClient } from '@tanstack/react-query';
import { startOfISOWeek } from 'date-fns';
import { useEffect, useRef } from 'react';
import { useClient } from 'urql';

import { hasEntitySummaryTab, useAuthStore } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import {
  COMPOSITION_HEADCOUNT_ONLY_V3,
  type CustomRoleTaxonomySelection,
  Dimension1,
  ENTITY_V2_QUERY,
  EntityV2Type,
  GET_SAVED_SETS,
  MonthWeekIndexQuery,
  PAY_V2_TOPGEOS_QUERY,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  POSTINGS_V3_GET_DATA,
  PositionCategoryKind,
  PositionsEntityKind,
  REFERENCE_LISTS_QUERY,
  SENTIMENT_SNAPSHOT,
  TALENT_DISCOVERY_MAP_DATA_V3,
  TAXONOMY_LATEST_VERSION_QUERY,
  TAXONOMY_LIST_QUERY,
  TAXONOMY_TREE_QUERY,
  Tab,
  TaxonomyType,
  WEEK_SELECTION_LIST_QUERY,
} from '@revelio/data-access';

import {
  RICS_FULL_TAXONOMY_TREE_LEVELS,
  RICS_TAXONOMY_ID,
} from '../../shared/selection-lists/constants';
import type { SelectionList } from '../../shared/selection-lists/types';
import { getRoleTaxonomyId as getRoleTreeTaxonomyId } from '../../shared/selection-lists/utils/roleTaxonomy';
import { normalizeTaxonomyTree } from '../../shared/selection-lists/utils/taxonomyTree';
import { treeToParentLinkedSelectionLists } from '../../shared/selection-lists/utils/treeToParentLinkedSelectionLists';
import { useUnleashFlag } from '../../shared/unleash-flags/use-unleash-flag';
import { getAllPreferences } from '../../shared/user-preferences/api/user-preferences-api';
import {
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../../shared/user-preferences/types';
import { getDefaultPreferences } from '../../shared/user-preferences/utils/preference-defaults';
import { PreferenceBrowserStorage } from '../../shared/user-preferences/utils/preference-session-storage';
import { mapRoleTaxonomySelectionToId } from '../../shared/user-preferences/utils/taxonomy-mapper';
import { getRoleTaxonomyId } from '../CustomDash/components/DataProvider/talentDiscovery/roles';
import { getSkillTaxonomyId } from '../CustomDash/components/DataProvider/talentDiscovery/skills';
import { createNewFilterState } from '../CustomDash/filters/engine/filterGroupConsistency';
import {
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
} from '../CustomDash/filters/types/filters';
import { getBaseCompositionV3BreakdownLevels } from '../CustomDash/filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import { getFormattedDateString } from '../CustomDash/filters/utils/dateUtils';
import { getPostingsV3Variables } from '../CustomDash/filters/utils/getPostingsV3Variables';
import { getActiveSelections } from '../CustomDash/filters/utils/primaryFilterUtils';
import { GLOBAL_MIN_START_DATE } from '../CustomDash/filters/utils/providerDateOverrides';
import { mapPositionsEntitiesToCompositionV3 } from '../CustomDash/filters/utils/role-skill-ent-kind';
import { SKILL_REFERENCE_TAXONOMY_TREE_LEVELS } from '../CustomDash/filters/utils/skillReferenceTaxonomyTree';
import { ROLE_TAXONOMY_TREE_LEVELS } from '../CustomDash/filters/utils/taxonomy-tree-levels';

/**
 * Prefetch overview page data while the user is on the landing page, reusing the
 * overview page's `createNewFilterState` path (selectionDefault: 1) so entity
 * selection, adaptive taxonomy, and filter construction stay aligned and the
 * warmed cache matches the page's requests.
 *
 * Priority order:
 * 1. Fetch the RICS tree, user preferences, and MonthWeekIndexQuery in parallel
 * 2. Compute adaptive taxonomy via createNewFilterState
 * 3. Fire chart queries for Company Overview (most common navigation path)
 * 4. Prefetch the adaptive role + skill taxonomy trees (lower priority)
 */
export const usePrefetchOverviewData = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const hasPrefetched = useRef(false);
  // Read the flag in the hook body and close over it (the prefetch runs in an
  // effect). PostingDataProvider resolves the same flag, so the warmed cache
  // matches whichever query the page issues.
  const isPostingsV3Enabled = useUnleashFlag(FeatureFlag.PostingsV3);

  useEffect(() => {
    if (hasPrefetched.current) return;
    if (!currentUser) return; // don't mark as prefetched; allow retry

    // Skip prefetch entirely if the user doesn't have access to the overview page
    const userTabs = currentUser.tabs ?? [];
    if (userTabs.length > 0 && !hasEntitySummaryTab(userTabs)) {
      hasPrefetched.current = true;
      return;
    }

    hasPrefetched.current = true;

    const prefetch = async () => {
      // Fire no-variable queries immediately (no dependencies).
      // Best-effort prefetches not awaited below — swallow rejections.
      const swallowError = () => {};
      // Skip global error handling (error dialogs + Kibana logs) for all
      // prefetch queries — failures are expected and the overview page will
      // re-fetch its own data.
      const prefetchContext = { skipGlobalErrorHandling: true };
      client
        .query(
          TAXONOMY_LIST_QUERY,
          { taxonomyType: TaxonomyType.Role },
          prefetchContext
        )
        .toPromise()
        .catch(swallowError);
      client
        .query(
          TAXONOMY_LATEST_VERSION_QUERY,
          { taxonomyType: TaxonomyType.Role },
          prefetchContext
        )
        .toPromise()
        .catch(swallowError);
      // Kept: the skill-tree warm below derives the page's taxonomyVersion
      // from these results (useSkillTaxonomyAdapter's sources).
      const skillTaxonomyListPromise = client
        .query(
          TAXONOMY_LIST_QUERY,
          { taxonomyType: TaxonomyType.Skill },
          prefetchContext
        )
        .toPromise();
      skillTaxonomyListPromise.catch(swallowError);
      const skillTaxonomyLatestVersionPromise = client
        .query(
          TAXONOMY_LATEST_VERSION_QUERY,
          { taxonomyType: TaxonomyType.Skill },
          prefetchContext
        )
        .toPromise();
      skillTaxonomyLatestVersionPromise.catch(swallowError);

      // Fetch the rics tree, preferences, and date index in parallel.
      // Variables must stay identical to useTaxonomyTree's (key order included)
      // or the overview page misses the cache and re-fetches.
      const [{ data: ricsData }, allPrefs, { data: dateIndexData }] =
        await Promise.all([
          client
            .query(
              TAXONOMY_TREE_QUERY,
              {
                taxonomyType: TaxonomyType.Rics,
                taxonomyId: RICS_TAXONOMY_ID,
                taxonomyVersion: undefined,
                levels: RICS_FULL_TAXONOMY_TREE_LEVELS,
                includeTitles: false,
              },
              prefetchContext
            )
            .toPromise(),
          queryClient.fetchQuery({
            queryKey: ['user-preferences'],
            queryFn: () => getAllPreferences(),
          }),
          client.query(MonthWeekIndexQuery, {}, prefetchContext).toPromise(),
        ]);

      // Write preferences to session storage so FilterProvider can read them
      // synchronously when it initializes. Without this, the react-query cache
      // being pre-populated causes useUserPreferences to skip the loading gate,
      // but the useEffect that writes to PreferenceBrowserStorage hasn't run yet.
      if (allPrefs) {
        Object.entries(allPrefs).forEach(([scopeKey, prefs]) => {
          const scope = scopeKey as PreferenceScope;
          const existing = PreferenceBrowserStorage.get(scope);
          PreferenceBrowserStorage.set(scope, {
            ...prefs,
            ...existing,
          } as UserPreferences);
        });
      }

      // Same tree → selection-list conversion as useSelectionListsForKeys, so
      // the adaptive taxonomy computed here matches the overview page's.
      const ricsTree = normalizeTaxonomyTree(ricsData?.taxonomy);
      const ricsK400 = treeToParentLinkedSelectionLists(
        ricsTree.nodes,
        ricsTree.levels
      ).rics_k400 as SelectionList<'rics_k400'> | undefined;
      const endMonth = dateIndexData?.index?.endMonth;
      const maxWeek = dateIndexData?.index?.maxWeek;
      if (!ricsK400?.length || !endMonth || !maxWeek) return;

      // Merge preferences: defaults + global + company scope
      const defaults = getDefaultPreferences(PreferenceScope.COMPANY);
      const prefs: UserPreferences = {
        ...defaults,
        ...allPrefs[PreferenceScope.GLOBAL],
        ...allPrefs[PreferenceScope.COMPANY],
      } as UserPreferences;
      // Company overview uses 'posting-provider-range' preset:
      //   start = GLOBAL_MIN_START_DATE, end = systemMax
      // Use UTCDate + getFormattedDateString to match overview page date processing
      const startDateMonth = getFormattedDateString(
        new UTCDate(GLOBAL_MIN_START_DATE),
        'month'
      );
      const endDateMonth = getFormattedDateString(
        new UTCDate(endMonth),
        'month'
      );
      const startDateWeek = getFormattedDateString(
        new UTCDate(GLOBAL_MIN_START_DATE),
        'week'
      );
      const endDateWeek = getFormattedDateString(new UTCDate(maxWeek), 'week');
      // Pay uses viewGranularity='week' which resolves systemMax from maxWeek,
      // then formats it as 'month' (yyyy-MM). This means pay's end_date is
      // the month derived from maxWeek, not endMonth.
      const endDateMonthFromWeek = getFormattedDateString(
        new UTCDate(maxWeek),
        'month'
      );

      // Resolve default selections from preferences (or hardcoded defaults)
      const userDefaultSelections =
        prefs[UserPreferenceKey.DefaultFilters] ?? [];

      // Build filter state using the same code path as the overview page.
      // selectionDefault: 1 matches company-overview-v2 template config.
      const filterState = createNewFilterState({
        primaryFilterId: FilterId.COMPANY,
        pageId: 'company-overview',
        selectionDefault: 1,
        ricsK400List: ricsK400,
        defaultSelections:
          userDefaultSelections.length > 0
            ? { [FilterId.COMPANY]: userDefaultSelections }
            : undefined,
      });

      const filterMap = filterState.groupMap.get(FilterId.COMPANY);
      if (!filterMap) return;

      // Extract the active company selection (single entity for overview)
      const companyFilter = filterMap.get(FilterId.COMPANY);
      if (!companyFilter || companyFilter.type !== FilterType.TREE) return;

      const activeSelections = getActiveSelections(
        companyFilter.primarySelections ?? []
      );
      if (activeSelections.length === 0) return;

      // Convert filter map to array for getRoleTaxonomyId / getSkillTaxonomyId
      const filtersArray = Array.from(filterMap.values());

      // Use the same functions the TD map data provider uses
      const rolesTax = getRoleTaxonomyId(filtersArray);
      const skillsTax = getSkillTaxonomyId(filtersArray);

      // Apply preference-controlled filter values
      const contingentWorkers =
        prefs[UserPreferenceKey.ContingentWorkers] ?? false;
      // Data Adjustments preference (true = "Unadjusted"). Only the
      // composition corrections honor it on the overview page: corrections
      // read the activeFilterMap, which the preference sync seeds, while the
      // V2 chart family builds filters from useEffectiveFilters, which only
      // emits template-declared filters — and the overview template does not
      // declare DATA_ADJUSTMENTS, so those queries always send `raw: false`.
      const dataAdjustments = prefs[UserPreferenceKey.DataAdjustments] ?? false;

      // Build entities from active selections
      const entities = activeSelections.map((sel) => ({
        EntType: PositionsEntityKind.Company,
        EntID: parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10),
      }));

      // Extract taxonomy values for non-TD queries
      const roleTaxonomyFilter = filterMap.get(FilterId.ROLE_TAXONOMY);
      const roleTaxonomy: CustomRoleTaxonomySelection =
        roleTaxonomyFilter?.type === FilterType.VALUE
          ? (roleTaxonomyFilter.value as CustomRoleTaxonomySelection)
          : { default: true };

      const skillTaxonomyFilter = filterMap.get(FilterId.SKILL_TAXONOMY);
      const skillTaxonomyId =
        skillTaxonomyFilter?.type === FilterType.VALUE
          ? ((skillTaxonomyFilter.value as { taxonomyId?: number })
              ?.taxonomyId ?? 0)
          : 0;

      // Shared filter base
      const baseFilters = {
        raw: false,
        contingent_workers: contingentWorkers,
        custom_role: { taxonomyId: roleTaxonomy },
        custom_skill: { taxonomy_id: skillTaxonomyId, from_new_refs: true },
      };

      // Fire queries — results land in URQL cache for instant render on overview page

      // Gate chart prefetches on user tab permissions to avoid firing queries
      // the user isn't authorized for. Mirrors RequireAuth tab-check behavior:
      // empty tabs = unrestricted; non-empty = only listed tabs.
      const canPrefetchTab = (tab: Tab) =>
        userTabs.length === 0 || userTabs.includes(tab);

      // Talent Discovery Map (highest priority)
      const companyExprNodes = activeSelections.map((sel) => ({
        val: {
          pos_attribute: {
            kind: PositionCategoryKind.CompanyId,
            id: parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10),
            curr: true,
          },
        },
      }));

      const firstNode = companyExprNodes[0];
      if (!firstNode) return;

      const companyExprNode =
        companyExprNodes.length === 1
          ? firstNode
          : { or: { children: companyExprNodes } };

      // All chart prefetches are best-effort — catch individually since they
      // are not awaited and would otherwise produce unhandled rejections.
      if (canPrefetchTab(Tab.TalentDiscovery)) {
        client
          .query(
            TALENT_DISCOVERY_MAP_DATA_V3,
            {
              query: {
                filters: {
                  expr: {
                    user_value: {
                      child: companyExprNode,
                    },
                  },
                  roles_tax: rolesTax,
                  skills_tax: skillsTax,
                },
                pagination: {
                  page_num: 1,
                  page_size: 5000,
                },
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Composition (headcount only)
      if (canPrefetchTab(Tab.Overview)) {
        const entitiesV3 = mapPositionsEntitiesToCompositionV3(entities) ?? [];
        client
          .query(
            COMPOSITION_HEADCOUNT_ONLY_V3,
            {
              entities: entitiesV3,
              filters: {
                start_date: startDateMonth,
                end_date: endDateMonth,
              },
              // Mirrors mapLegacyRawToV3WeightFlags: with_weight = raw !== true.
              corrections: {
                with_contingent_workers: contingentWorkers,
                with_sample_weight: true,
                with_weight: !dataAdjustments,
              },
              role_taxonomy: mapRoleTaxonomySelectionToId(roleTaxonomy),
              skill_taxonomy: skillTaxonomyId,
              breakdown_options: getBaseCompositionV3BreakdownLevels(),
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Pay (top geos) — uses week granularity, so systemMax = maxWeek formatted as month
      if (canPrefetchTab(Tab.Compensation)) {
        client
          .query(
            PAY_V2_TOPGEOS_QUERY,
            {
              req: {
                entities,
                filters: {
                  ...baseFilters,
                  start_date: startDateMonth,
                  end_date: endDateMonthFromWeek,
                },
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Sentiment (snapshot — overview uses isSnapshot:true → both dates = max)
      if (canPrefetchTab(Tab.Sentiment)) {
        client
          .query(
            SENTIMENT_SNAPSHOT,
            {
              dim1: Dimension1.Company,
              filters: {
                company: activeSelections.map((sel) =>
                  parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10)
                ),
                custom_role: { taxonomyId: roleTaxonomy },
                start_date: endDateMonth,
                end_date: endDateMonth,
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Postings — flag-on warms `postings_v3` (plus the week id->date map
      // `usePostingsV3WeekRefs` reads); flag-off warms the legacy `posting` query.
      if (canPrefetchTab(Tab.Posting)) {
        if (isPostingsV3Enabled) {
          // Reuse the provider's variable builder so the warmed cache key is
          // byte-identical to PostingDataProviderV3's. The overview template
          // declares neither METRIC_MODE nor POSTINGS_SOURCE, so the builder
          // falls back to `defaultMetricMode` ('posting') + the unified provider
          // — matched here by passing the filter-map values with only the date
          // range resolved.
          const postingsV3EffectiveFilters: Filter[] = Array.from(
            filterMap.values()
          ).map((filter): Filter => {
            if (filter.id !== FilterId.DATE_RANGE) return filter;
            // Reproduce the page's `posting-provider-range` preset (start =
            // GLOBAL_MIN_START_DATE, end = systemMax) snapped to ISO week start;
            // getPostingFilters then formats + clamps identically.
            return {
              ...(filter as DateRangeFilter),
              min: startOfISOWeek(
                new UTCDate(GLOBAL_MIN_START_DATE)
              ) as UTCDate,
              max: startOfISOWeek(new UTCDate(maxWeek)) as UTCDate,
            } as DateRangeFilter;
          });

          const postingsV3Variables = getPostingsV3Variables({
            primaryDimension: FilterId.COMPANY,
            effectiveFilters: postingsV3EffectiveFilters,
            // Company overview uses the 'posting' metric mode (mirrors
            // PostingDataProvider's defaultMetricMode for COMPANY_OVERVIEW_V2).
            defaultMetricMode: 'posting',
            activeFilterMap: filterMap,
          });

          client
            .query(POSTINGS_V3_GET_DATA, postingsV3Variables, prefetchContext)
            .toPromise()
            .catch(swallowError);
          // Warm the week id->date map (no variables); the provider pauses its
          // posting query until this resolves, so warming it avoids a stall.
          client
            .query(WEEK_SELECTION_LIST_QUERY, {}, prefetchContext)
            .toPromise()
            .catch(swallowError);
        } else {
          client
            .query(
              POSTING_GET_DATA,
              {
                dim1: Dimension1.Company,
                filters: {
                  company: activeSelections.map((sel) =>
                    parseInt(sel.id.split('_').slice(-1)[0] ?? '0', 10)
                  ),
                  custom_role: { taxonomyId: roleTaxonomy },
                  custom_skill: {
                    taxonomy_id: skillTaxonomyId,
                    from_new_refs: true,
                  },
                  provider: [POSTING_SOURCE_IDS.unified],
                  metric_mode: 'posting',
                  start_date: startDateWeek,
                  end_date: endDateWeek,
                },
              },
              prefetchContext
            )
            .toPromise()
            .catch(swallowError);
        }
      }

      // Entity summary (description + competitors)
      const firstEntity = entities[0];
      if (
        firstEntity &&
        (userTabs.length === 0 || hasEntitySummaryTab(userTabs))
      ) {
        client
          .query(
            ENTITY_V2_QUERY,
            {
              req: {
                entID: firstEntity.EntID,
                entType: EntityV2Type.Company,
                numCompetitors: 7,
              },
            },
            prefetchContext
          )
          .toPromise()
          .catch(swallowError);
      }

      // Prefetch taxonomy-specific selection lists (lower priority than chart data)

      // Saved sets feed the entity selector's saved-sets menu — pure UX warm,
      // so it shouldn't compete with the render-gating and chart queries above.
      client
        .query(GET_SAVED_SETS, {}, { ...prefetchContext, priority: 'low' })
        .toPromise()
        .catch(swallowError);

      // Role taxonomy tree — warm the tree the overview filters read via
      // useAllSelectionLists → useRoleTreeSelectionLists. Variables must match
      // that hook's useTaxonomyTree request (taxonomyId, levels, includeTitles,
      // key order) or the page misses the warmed cache entry.
      // ROLE_TAXONOMY_TREE_LEVELS [1,3,5,7] mirrors useAllSelectionLists' role
      // keys (role_k7 / role_k150 / role_k1500 / role_k15000).
      client
        .query(
          TAXONOMY_TREE_QUERY,
          {
            taxonomyType: TaxonomyType.Role,
            // `?? 0` mirrors useTaxonomyTree's coercion so the warmed cache key
            // matches the page's request.
            taxonomyId: getRoleTreeTaxonomyId(roleTaxonomy) ?? 0,
            taxonomyVersion: undefined,
            levels: ROLE_TAXONOMY_TREE_LEVELS,
            includeTitles: true,
          },
          { ...prefetchContext, priority: 'low' }
        )
        .toPromise()
        .catch(swallowError);

      // Skill taxonomy tree with the adaptive taxonomy — the page's skill
      // source (useCustomSkillTaxonomyTree), which resolves its version to the
      // latest one provided some listed taxonomy carries it (mirrors
      // useSkillTaxonomyAdapter). Variables must stay identical to
      // useTaxonomyTree's or the page misses the cache.
      const skillTaxonomyVersion = await Promise.all([
        skillTaxonomyListPromise,
        skillTaxonomyLatestVersionPromise,
      ])
        .then(([listResult, latestResult]) => {
          const latest = latestResult.data?.taxonomyLatestVersion.version;
          const taxonomies = listResult.data?.taxonomyList.taxonomies;
          return latest != null &&
            taxonomies?.some((taxonomy) => taxonomy.version === latest)
            ? latest
            : undefined;
        })
        .catch(() => undefined);
      client
        .query(
          TAXONOMY_TREE_QUERY,
          {
            taxonomyType: TaxonomyType.Skill,
            taxonomyId: skillTaxonomyId,
            taxonomyVersion: skillTaxonomyVersion,
            levels: [...SKILL_REFERENCE_TAXONOMY_TREE_LEVELS],
            includeTitles: true,
          },
          { ...prefetchContext, priority: 'low' }
        )
        .toPromise()
        .catch(swallowError);

      // Geography tree (MSA) — the overview's geography filter reads this via
      // useAllSelectionLists → useTaxonomyTree. Variables must match that hook's
      // request (GEO_TAXONOMY_ID 1, levels [1,2,3,4], no titles) or the page
      // misses the warmed cache entry. (The administrative tree, taxonomyId 2,
      // is mode-dependent and warmed on demand, not here.)
      client
        .query(
          TAXONOMY_TREE_QUERY,
          {
            taxonomyType: TaxonomyType.Geography,
            taxonomyId: 1,
            taxonomyVersion: undefined,
            levels: [1, 2, 3, 4],
            includeTitles: false,
          },
          { ...prefetchContext, priority: 'low' }
        )
        .toPromise()
        .catch(swallowError);

      // Reference lists (gender / ethnicity / education / seniority / …) — the
      // overview filters read these via useAllSelectionLists' REFERENCE_LISTS_QUERY.
      client
        .query(
          REFERENCE_LISTS_QUERY,
          {},
          { ...prefetchContext, priority: 'low' }
        )
        .toPromise()
        .catch(swallowError);
    };

    prefetch().catch(() => {
      // Prefetch is best-effort — if any query fails (e.g. missing auth
      // token on initial render), the overview page will fetch its own data.
    });
  }, [client, currentUser, queryClient, isPostingsV3Enabled]);
};
